import { useState, useEffect, useMemo, useRef } from 'react';
import { Link, useParams } from 'react-router-dom';
import SEOHead from '../../components/SEOHead';
import GreekSpinner from '../../components/GreekSpinner';
import NotFound from '../NotFound';
import AdSense from '../../components/AdSense';
import renderMathInElement from 'katex/contrib/auto-render';
import 'katex/dist/katex.min.css';
import BrownianMotionViz from '../../components/visualizations/BrownianMotionViz';

// Registry: maps data-viz id → React component
const VIZ_REGISTRY = {
  'brownian-motion': BrownianMotionViz,
};

// Split html_content into alternating html/viz chunks
function parseChunks(html) {
  const chunks = [];
  const regex  = /<div data-viz="([^"]+)"><\/div>/g;
  let last = 0, match;
  while ((match = regex.exec(html)) !== null) {
    if (match.index > last) chunks.push({ type: 'html', html: html.slice(last, match.index) });
    chunks.push({ type: 'viz', id: match[1] });
    last = regex.lastIndex;
  }
  if (last < html.length) chunks.push({ type: 'html', html: html.slice(last) });
  return chunks;
}

function extractToc(htmlContent) {
  const matches = [...htmlContent.matchAll(/<h([23])\s+id="([^"]+)"[^>]*>(.*?)<\/h[23]>/gi)];
  return matches.map(m => ({
    level: parseInt(m[1]),
    id: m[2],
    text: m[3].replace(/<[^>]+>/g, ''),
  }));
}

function TableOfContents({ items }) {
  if (items.length < 3) return null;
  return (
    <nav className="bg-surface rounded-clay border border-surface p-5 mb-8">
      <p className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">In this article</p>
      <ol className="space-y-1.5">
        {items.map(item => (
          <li key={item.id} style={{ paddingLeft: item.level === 3 ? '1rem' : '0' }}>
            <a
              href={`#${item.id}`}
              className="text-sm text-primary-600 dark:text-primary-400 hover:underline"
            >
              {item.text}
            </a>
          </li>
        ))}
      </ol>
    </nav>
  );
}

function RelatedPostCard({ post }) {
  const dateStr = new Date(post.date).toLocaleDateString('en-IN', {
    day: 'numeric', month: 'short', year: 'numeric',
  });
  return (
    <Link
      to={`/blog/${post['theme__slug']}/${post.slug}`}
      className="block bg-surface rounded-clay shadow-clay dark:shadow-clay-dark border border-surface p-4 hover:shadow-clay-hover dark:hover:shadow-clay-dark-hover transition-all duration-200 group"
    >
      <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100 group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors line-clamp-2">
        {post.title}
      </h4>
      <p className="mt-1 text-xs text-gray-500 dark:text-gray-400 line-clamp-2">
        {post.seo_description}
      </p>
      <div className="mt-2 text-xs text-gray-400 dark:text-gray-500">
        {post.read_time} min · {dateStr}
      </div>
    </Link>
  );
}

export default function BlogPost() {
  const { theme: themeSlug, slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    setLoading(true);
    setError(null);
    fetch(`/api/blog/posts/${slug}/?theme=${themeSlug}`)
      .then(r => {
        if (!r.ok) throw new Error('Not found');
        return r.json();
      })
      .then(setPost)
      .catch(() => setError('Article not found'))
      .finally(() => setLoading(false));
  }, [themeSlug, slug]);

  const articleRef = useRef(null);

  useEffect(() => {
    if (!articleRef.current || !post) return;
    renderMathInElement(articleRef.current, {
      delimiters: [
        { left: '\\[', right: '\\]', display: true },
        { left: '\\(', right: '\\)', display: false },
      ],
      throwOnError: false,
    });
  }, [post]);

  const toc = useMemo(
    () => (post ? extractToc(post.html_content) : []),
    [post]
  );

  const dateStr = post
    ? new Date(post.date).toLocaleDateString('en-IN', {
        day: 'numeric', month: 'long', year: 'numeric',
      })
    : '';

  return (
    <>
      {post && <SEOHead
        title={post.title}
        description={post.seo_description}
        keywords={post.seo_keywords}
        canonicalPath={`/blog/${post.theme_slug}/${post.slug}`}
        image={post.featured_image || null}
        article={{ author: post.author, datePublished: post.date }}
      />}

      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-5">
        {loading ? (
          <div className="flex justify-center py-24"><GreekSpinner /></div>
        ) : error || !post ? (
          <NotFound />
        ) : (
        <>
        {/* Back button */}
        <div className="mb-4">
          <Link to={`/blog/${post.theme_slug}`} className="inline-flex items-center gap-1 text-xs text-neutral-400 dark:text-neutral-500 hover:text-primary-600 dark:hover:text-primary-400 transition-colors">
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" /></svg>
            Back to {post.theme_name}
          </Link>
        </div>
        {/* Breadcrumb */}
        <div className="flex items-center gap-2 text-sm text-gray-400 dark:text-gray-500 mb-8">
          <Link to="/blog" className="hover:text-primary-600 dark:hover:text-primary-400">Blog</Link>
          <span>/</span>
          <Link to={`/blog/${post.theme_slug}`} className="hover:text-primary-600 dark:hover:text-primary-400">
            {post.theme_name}
          </Link>
          <span>/</span>
          <span className="text-gray-600 dark:text-gray-300 truncate">{post.title}</span>
        </div>

        {/* Article header */}
        <header className="mb-8">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-gray-100 leading-tight">
            {post.title}
          </h1>
          <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-gray-500 dark:text-gray-400">
            <span>By {post.author}</span>
            <span>·</span>
            <span>{dateStr}</span>
            <span>·</span>
            <span>{post.read_time} min read</span>
          </div>
          {post.tags && post.tags.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-2">
              {post.tags.map(tag => (
                <span
                  key={tag}
                  className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300"
                >
                  {tag}
                </span>
              ))}
            </div>
          )}
        </header>

        {/* Table of contents */}
        <TableOfContents items={toc} />

        {/* Article content */}
        <article
          ref={articleRef}
          className="prose dark:prose-invert max-w-none
            prose-headings:font-bold prose-headings:tracking-tight
            prose-h2:text-xl prose-h2:mt-10 prose-h2:mb-4
            prose-h3:text-lg prose-h3:mt-8 prose-h3:mb-3
            prose-p:leading-7
            prose-a:no-underline hover:prose-a:underline
            prose-code:text-sm prose-code:px-1.5 prose-code:py-0.5 prose-code:rounded prose-code:before:content-none prose-code:after:content-none
            prose-pre:rounded-clay-sm
            prose-table:text-sm"
        >
          {parseChunks(post.html_content).map((chunk, i) => {
            if (chunk.type === 'viz') {
              const Viz = VIZ_REGISTRY[chunk.id];
              return Viz ? <Viz key={i} /> : null;
            }
            return <div key={i} dangerouslySetInnerHTML={{ __html: chunk.html }} />;
          })}
        </article>

        {/* Ad */}
        <AdSense adSlot="" label="Blog Post Banner" adStyle={{ minHeight: 250 }} />

        {/* CTA */}
        <div className="mt-12 bg-primary-50 dark:bg-primary-900/20 rounded-clay border border-primary-200 dark:border-primary-800/50 p-6">
          <h3 className="text-base font-semibold text-primary-900 dark:text-primary-100">
            Try It Yourself
          </h3>
          <p className="mt-1.5 text-sm text-primary-700 dark:text-primary-300">
            See live Greeks — Delta, Gamma, Theta, Vega, and Rho — for RELIANCE, NIFTY, and all NSE F&O symbols, calculated from real Bhavcopy data.
          </p>
          <div className="mt-4 flex flex-wrap gap-3">
            <Link
              to="/options-breakdown"
              className="inline-flex items-center px-4 py-2 rounded-clay-sm bg-primary-600 hover:bg-primary-700 text-white text-sm font-medium shadow-clay-btn transition-colors"
            >
              Option Analysis
            </Link>
            <Link
              to="/iv-calculator"
              className="inline-flex items-center px-4 py-2 rounded-clay-sm bg-white/80 dark:bg-white/8 border border-primary-200 dark:border-primary-700 text-primary-700 dark:text-primary-300 text-sm font-medium shadow-clay-btn dark:shadow-clay-btn-dark transition-colors hover:bg-primary-50 dark:hover:bg-primary-900/20"
            >
              IV Calculator
            </Link>
          </div>
        </div>

        {/* Related posts */}
        {post.related && post.related.length > 0 && (
          <div className="mt-12">
            <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100 mb-4">
              More in {post.theme_name}
            </h3>
            <div className="grid sm:grid-cols-3 gap-4">
              {post.related.map(rel => (
                <RelatedPostCard key={rel.slug} post={rel} />
              ))}
            </div>
          </div>
        )}

        {/* Back link */}
        <div className="mt-10 pt-6 border-t border-gray-200 dark:border-gray-700">
          <Link
            to={`/blog/${post.theme_slug}`}
            className="flex items-center gap-1.5 text-sm text-primary-600 dark:text-primary-400 hover:underline"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to {post.theme_name}
          </Link>
        </div>
        </>
        )}
      </div>
    </>
  );
}
